import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { FaMapMarkerAlt, FaTrash } from "react-icons/fa";
import { getLocalSelling, saveLocalSelling, geocodeLocalSellingAddress } from "../auth";
import GeoCascadeFields, { EMPTY_GEO_VALUE } from "./GeoCascadeFields";
import countryList from "../../countries.json";
import "./LocalSellingModal.css";
import { elakayAutoText } from "./dataElakay";


// Single shared "Target Location" / "Local Selling" configuration modal.
// Opened identically from User Settings and from the shortcut button on
// /myshop's header (headerShopSeller.js) -- same component, same state, same
// endpoint, same saved data (mission 2.2). Never fork this into two forms.
const EMPTY_CONFIG = {
  enabled: false,
  mode: [],
  countries: [],
  statesOrProvinces: [],
  cities: [],
  postalCodes: [],
  radiusZones: [],
  // Zones built with the Country -> State -> City -> ZIP cascade (same
  // geographic data as My Profile -> Add Address). The flat lists and
  // radiusZones above are zones saved before the cascade existed: still
  // shown, still matched, removable.
  zones: []
};
const LEGACY_LISTS = ["countries", "statesOrProvinces", "cities", "postalCodes"];
const MAX_RADIUS_MILES = 100;
const countryNameOf = (code) => countryList.find((c) => c.code === code)?.name || code;

const RADIUS_OPTIONS = [5, 10, 20, 25, 50];

const LocalSellingModal = ({ showModal, setShowModal, infoSource, token, onSaved }) => {
  const tr = (key, fallback) =>
    infoSource?.laguangeElakay?.("localSelling", infoSource.languageAPI)?.[key] || fallback;

  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Zone builder: one cascade selection + an optional delivery radius.
  const [zoneGeo, setZoneGeo] = useState(EMPTY_GEO_VALUE);
  const [zoneRadius, setZoneRadius] = useState("");
  const [zoneUnit, setZoneUnit] = useState("mi");
  const [editingIndex, setEditingIndex] = useState(null);
  const [addingZone, setAddingZone] = useState(false);

  useEffect(() => {
    if (!showModal || !token) return;
    setLoading(true);
    setError("");
    setSuccess("");
    getLocalSelling(token).then((data) => {
      setLoading(false);
      if (data?.success) {
        setConfig({ ...EMPTY_CONFIG, ...data.localSelling });
      } else {
        setError(data?.error || tr("loadError", elakayAutoText("h_3f190e78ce2a6b", "Unable to load local selling settings.")));
      }
    });
  }, [showModal, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const trv = (key, fallback, values = {}) =>
    String(tr(key, fallback)).replace(/\{(\w+)\}/g, (m, name) => (name in values ? String(values[name]) : m));

  const removeListValue = (listKey, value) => {
    setConfig((prev) => ({ ...prev, [listKey]: prev[listKey].filter((v) => v !== value) }));
  };
  const removeLegacyRadiusZone = (index) => {
    setConfig((prev) => ({ ...prev, radiusZones: prev.radiusZones.filter((_, i) => i !== index) }));
  };

  const resetBuilder = () => {
    setZoneGeo(EMPTY_GEO_VALUE);
    setZoneRadius("");
    setZoneUnit("mi");
    setEditingIndex(null);
  };

  const addOrUpdateZone = async () => {
    setError("");
    setSuccess("");
    const geo = { ...EMPTY_GEO_VALUE, ...zoneGeo };
    if (!geo.country) {
      setError(tr("countryRequired", "Select a country for this zone."));
      return;
    }
    const radius = zoneRadius === "" ? null : Number(zoneRadius);
    if (radius !== null) {
      const miles = zoneUnit === "km" ? radius / 1.60934 : radius;
      if (!Number.isFinite(radius) || radius <= 0 || miles > MAX_RADIUS_MILES) {
        setError(trv("radiusInvalid", "The delivery radius must be between 1 and {max} miles.", { max: MAX_RADIUS_MILES }));
        return;
      }
      if (!geo.city && !geo.postalCode) {
        setError(tr("radiusNeedsCenter", "Choose a city or a ZIP / postal code as the center of the delivery radius."));
        return;
      }
      if (!Number.isFinite(Number(geo.latitude)) || geo.latitude === null) {
        // Country without place list: locate the typed place.
        setAddingZone(true);
        const query = [geo.postalCode, geo.city, geo.stateOrProvince, geo.countryName || countryNameOf(geo.country)].filter(Boolean).join(", ");
        const data = await geocodeLocalSellingAddress(token, query);
        setAddingZone(false);
        if (!data?.success) {
          setError(data?.error || tr("geocodeError", "No location found for this search."));
          return;
        }
        geo.latitude = data.latitude;
        geo.longitude = data.longitude;
      }
    }
    const zone = {
      country: geo.country,
      countryName: geo.countryName || countryNameOf(geo.country),
      stateOrProvince: geo.stateOrProvince,
      stateCode: geo.stateCode,
      city: geo.city,
      postalCode: geo.postalCode,
      latitude: geo.latitude ?? null,
      longitude: geo.longitude ?? null,
      radius,
      unit: zoneUnit,
    };
    setConfig((prev) => {
      const zones = [...(prev.zones || [])];
      if (editingIndex !== null && editingIndex < zones.length) zones[editingIndex] = zone;
      else zones.push(zone);
      return { ...prev, zones };
    });
    resetBuilder();
  };

  const editZone = (index) => {
    const zone = config.zones[index];
    if (!zone) return;
    setZoneGeo({ ...EMPTY_GEO_VALUE, ...zone });
    setZoneRadius(zone.radius ? String(zone.radius) : "");
    setZoneUnit(zone.unit === "km" ? "km" : "mi");
    setEditingIndex(index);
    setError("");
  };

  const removeZone = (index) => {
    setConfig((prev) => ({ ...prev, zones: prev.zones.filter((_, i) => i !== index) }));
    if (editingIndex === index) resetBuilder();
  };

  const describeZone = (zone) => {
    const place = [zone.postalCode, zone.city, zone.stateOrProvince, zone.countryName || countryNameOf(zone.country)].filter(Boolean).join(", ");
    return zone.radius
      ? `${zone.radius} ${zone.unit === "km" ? "km" : tr("miles", "miles")} ${tr("around", "around")} ${place}`
      : place;
  };

  const handleReset = () => {
    setConfig((prev) => ({ ...EMPTY_CONFIG, enabled: prev.enabled }));
    resetBuilder();
  };

  const handleDisable = () => {
    setConfig((prev) => ({ ...prev, enabled: false }));
  };

  const handleClose = () => {
    setShowModal(false);
    resetBuilder();
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    const data = await saveLocalSelling(token, config);
    setSaving(false);
    if (!data?.success) {
      setError(data?.error || tr("saveError", elakayAutoText("h_c611cffeb6133a", "Unable to save local selling settings.")));
      return;
    }
    setConfig({ ...EMPTY_CONFIG, ...data.localSelling });
    setSuccess(tr("saveSuccess", elakayAutoText("h_0175bb40770e3b", "Local selling settings saved.")));
    onSaved?.(data.localSelling);
  };

  // Human-readable summary, built live from the not-yet-saved state --
  // mission 2.3.6: "Ce shop vend à Brooklyn, Queens et dans un rayon de 20
  // miles autour du ZIP code 11201."
  const buildSummary = () => {
    if (!config.enabled) return tr("summaryEverywhere", "This shop sells everywhere the platform already allows.");
    const parts = [
      ...(config.zones || []).map(describeZone),
      ...config.cities,
      ...config.statesOrProvinces,
      ...config.countries,
      ...config.postalCodes.map((zip) => tr("zipPrefix", "ZIP") + " " + zip),
      ...config.radiusZones.map((z) => `${z.radius}${z.unit} ${tr("around", "around")} ${z.label || z.formattedAddress || `${z.latitude},${z.longitude}`}`)
    ];
    if (!parts.length) return tr("summaryNoZones", "Local selling is enabled but no area has been added yet -- this shop won't be visible to any buyer until you add one.");
    return tr("summaryPrefix", "This shop sells in") + " " + parts.join(", ");
  };

  const badgeList = (listKey, labelPrefix = "") => (
    <div className="local-selling-badges">
      {config[listKey].map((value) => (
        <span key={value} className="local-selling-badge">
          {labelPrefix}{value}
          <button type="button" onClick={() => removeListValue(listKey, value)} aria-label={tr("remove", "Remove")}>×</button>
        </span>
      ))}
    </div>
  );

  return (
    <Modal show={showModal} onHide={handleClose} size="lg" centered dialogClassName="local-selling-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaMapMarkerAlt style={{ marginRight: 8 }} />
          {tr("modalTitle", "Target Location")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <p>{tr("loading", "Loading…")}</p>}
        {!loading && (
          <>
            <label className="local-selling-switch-row">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) => setConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
              <span>{tr("enableToggle", "Enable local selling / targeted location")}</span>
            </label>

            {!config.enabled && (
              <p className="local-selling-hint">
                {tr("disabledHint", "This shop stays visible in every territory the platform already allows -- no geographic restriction applied.")}
                {(config.zones || []).length > 0 && (
                  <> {trv("zonesKept", "Your {count} zone(s) are kept and apply again when you re-enable local selling.", { count: config.zones.length })}</>
                )}
              </p>
            )}

            {config.enabled && (
              <>
                <div className="local-selling-field-group local-selling-builder">
                  <div className="local-selling-builder__title">
                    {editingIndex !== null
                      ? trv("editZoneTitle", "Edit zone {n}", { n: editingIndex + 1 })
                      : tr("newZoneTitle", "New zone")}
                  </div>
                  <GeoCascadeFields value={zoneGeo} onChange={setZoneGeo} tr={tr} idPrefix="target-zone" />
                  <div className="local-selling-radius">
                    <label htmlFor="target-zone-radius">{tr("deliveryRadius", "Delivery radius")}</label>
                    <div className="local-selling-add-row">
                      <select
                        id="target-zone-radius"
                        value={zoneRadius === "" ? "" : (RADIUS_OPTIONS.includes(Number(zoneRadius)) ? String(zoneRadius) : "custom")}
                        onChange={(e) => setZoneRadius(e.target.value === "custom" ? (zoneRadius || "15") : e.target.value)}
                      >
                        <option value="">{tr("wholeArea", "Whole selected area (no radius)")}</option>
                        {RADIUS_OPTIONS.map((mi) => <option key={mi} value={String(mi)}>{mi}</option>)}
                        <option value="custom">{tr("customRadius", "Custom")}</option>
                      </select>
                      {zoneRadius !== "" && !RADIUS_OPTIONS.includes(Number(zoneRadius)) && (
                        <input
                          type="number"
                          min={1}
                          max={MAX_RADIUS_MILES}
                          className="local-selling-custom-radius"
                          aria-label={tr("customRadius", "Custom")}
                          value={zoneRadius}
                          onChange={(e) => setZoneRadius(e.target.value)}
                        />
                      )}
                      {zoneRadius !== "" && (
                        <select value={zoneUnit} onChange={(e) => setZoneUnit(e.target.value)} aria-label={tr("radiusUnit", "Unit")}>
                          <option value="mi">{tr("miles", "miles")}</option>
                          <option value="km">{tr("kilometers", "km")}</option>
                        </select>
                      )}
                    </div>
                    <p className="local-selling-hint">{tr("radiusCascadeHint", "Optional: limit this zone to a distance around the selected city or ZIP / postal code.")}</p>
                  </div>
                  <div className="local-selling-add-row local-selling-builder__actions">
                    <button type="button" onClick={addOrUpdateZone} disabled={addingZone || !zoneGeo.country}>
                      {addingZone ? tr("searching", "Searching…") : editingIndex !== null ? tr("updateZone", "Update zone") : tr("addZone", "Add a zone")}
                    </button>
                    {editingIndex !== null && (
                      <button type="button" className="local-selling-btn-secondary" onClick={resetBuilder}>{tr("cancelEdit", "Cancel edit")}</button>
                    )}
                  </div>
                </div>

                {(config.zones || []).length > 0 && (
                  <div className="local-selling-field-group">
                    <label>{tr("zones", "Zones")}</label>
                    <ul className="local-selling-zone-list">
                      {config.zones.map((zone, index) => (
                        <li key={`${zone.country}-${zone.stateOrProvince}-${zone.city}-${zone.postalCode}-${index}`} className={editingIndex === index ? "is-editing" : ""}>
                          <span><strong>{trv("zoneNumber", "Zone {n}", { n: index + 1 })}</strong> · {describeZone(zone)}</span>
                          <span className="local-selling-zone-actions">
                            <button type="button" className="local-selling-zone-edit" onClick={() => editZone(index)}>{tr("edit", "Edit")}</button>
                            <button type="button" onClick={() => removeZone(index)} aria-label={`${tr("remove", "Remove")} ${trv("zoneNumber", "Zone {n}", { n: index + 1 })}`}><FaTrash /></button>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(LEGACY_LISTS.some((key) => (config[key] || []).length) || (config.radiusZones || []).length > 0) && (
                  <div className="local-selling-field-group">
                    <label>{tr("previousZones", "Areas saved before")}</label>
                    <p className="local-selling-hint">{tr("previousZonesHint", "Still applied. Remove them and add them again with the list above to make them precise.")}</p>
                    {badgeList("countries")}
                    {badgeList("statesOrProvinces")}
                    {badgeList("cities")}
                    {badgeList("postalCodes", `${tr("zipPrefix", "ZIP")} `)}
                    <ul className="local-selling-zone-list">
                      {(config.radiusZones || []).map((zone, index) => (
                        <li key={`${zone.latitude}-${zone.longitude}-${index}`}>
                          <span>{zone.radius}{zone.unit} {tr("around", "around")} {zone.label || zone.formattedAddress}</span>
                          <button type="button" onClick={() => removeLegacyRadiusZone(index)} aria-label={tr("remove", "Remove")}><FaTrash /></button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            <div className="local-selling-summary">{buildSummary()}</div>

            {error && <div className="local-selling-alert local-selling-alert-error">{error}</div>}
            {success && <div className="local-selling-alert local-selling-alert-success">{success}</div>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="local-selling-btn local-selling-btn-ghost" onClick={handleReset}>
          {tr("reset", "Reset")}
        </button>
        <button type="button" className="local-selling-btn local-selling-btn-ghost" onClick={handleDisable}>
          {tr("disable", "Sell everywhere")}
        </button>
        <button type="button" className="local-selling-btn local-selling-btn-ghost" onClick={handleClose}>
          {tr("cancel", "Cancel")}
        </button>
        <button type="button" className="local-selling-btn local-selling-btn-primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? tr("saving", "Saving…") : tr("save", "Save location")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default LocalSellingModal;
